import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getAuthTokenSelector, getPendingSelector } from '../core/selectors';
import { authRefresh } from '../core/actions';
import { readToken } from '../utils/readToken';

interface IUseAuthHook {
  refreshToken: VoidFunction;
}

export const useAuth = (): IUseAuthHook => {
  const dispatch = useDispatch();
  const accessToken = useSelector(getAuthTokenSelector);
  const pending = useSelector(getPendingSelector);

  const refreshToken = useCallback(() => {
    if (!accessToken || pending) return;

    try {
      const accessTokenDecoded = readToken(accessToken);

      const currentDateTimestamp = Math.round(new Date().getTime() / 1000);

      if (accessTokenDecoded?.exp && currentDateTimestamp > +accessTokenDecoded?.exp - 30) {
        dispatch(authRefresh({ accessToken }));
      }
    } catch (e) {
      console.warn('[Auth] Token is malformed/unreadable — triggering refresh', e);
      dispatch(authRefresh({ accessToken }));
    }
  }, [accessToken, pending, dispatch]);

  useEffect(() => {
    refreshToken();
  }, [refreshToken]);

  useEffect(() => {
    const scheduleTokenRefresh = setInterval(refreshToken, 60000);

    return () => {
      clearInterval(scheduleTokenRefresh);
    };
  }, [refreshToken]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshToken]);

  return { refreshToken };
};
